// AUTO-GENERATED from servers/common-servers-v2/helpers/FormHelper/validate_structure.js by servers/common-servers-v2/scripts/sync-form-helper.js.
// Do not edit: change the source and run `node scripts/sync-form-helper.js`.
/* eslint-disable */
import {
    BASE_FIELD_TYPES,
    OPTION_FIELD_TYPES,
    CUSTOM_FIELD_PREFIX,
    CUSTOM_FIELD_KEY_PATTERN,
    CUSTOM_VALUE_TYPES,
    KEY_PATTERN,
    LIMITS,
    GRID_SIZES,
    DATE_MODES,
    FILE_FORMATS,
    OPERATORS,
    UNARY_OPERATORS,
    VALIDATION_FIELD_TYPES,
    FIELD_DIRECTIONS,
} from "./constants.js";
import { condition_field_refs } from "./conditions.js";
import { check_regex } from "./regex.js";

/*
 * validate_form_structure(version, { template }) -> { valid, errors, warnings }
 *
 * Run before a version is saved (spec §3, §4, §5, §6). `version` is
 * { groups: [{ key, title, description, is_repeatable, <flags & conditions>, fields: [...] }] }.
 * `template` (optional) is the site's TemplateModel slice:
 * { form_supported_field_types: [String], form_custom_fields: [{ key, value_type }] }.
 * Without it only the shape of `custom:<key>` types is checked.
 *
 * Each issue is { code, path, key?, ...details }; `path` points into the version
 * (`groups.0.fields.2.options.1`). Codes are stable: the panel maps them to texts.
 */

const CONDITION_LISTS = ["show_conditions", "disabled_conditions", "required_conditions"];
const FLAGS = ["is_show", "is_disabled", "is_required"];
// Rules on a field's own value: conditions that must hold for the value to be accepted. Only a number or a price field has them. They
// read other fields (and the field itself) but change nothing about any state, so they are not part of the dependency graph.
const VALIDATION_LIST = "validation_conditions";

const is_plain_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const is_non_empty_string = (value) => typeof value === "string" && value.trim().length > 0;

const as_list = (value) => (Array.isArray(value) ? value : []);

const validate_form_structure = (version, { template } = {}) => {
    const errors = [];
    const warnings = [];
    const add_error = (code, path, extra = {}) => errors.push({ code, path, ...extra });
    const add_warning = (code, path, extra = {}) => warnings.push({ code, path, ...extra });

    if (!is_plain_object(version) || !Array.isArray(version.groups)) {
        add_error("invalid_structure", "groups");
        return { valid: false, errors, warnings };
    }
    const groups = version.groups;
    if (groups.length > LIMITS.GROUPS_MAX) {
        add_error("too_many_groups", "groups", { max: LIMITS.GROUPS_MAX });
    }

    const supported_types = as_list(template?.form_supported_field_types);
    const custom_fields = {};
    as_list(template?.form_custom_fields).forEach((item) => {
        if (item && typeof item.key === "string") {
            custom_fields[item.key] = item;
        }
    });
    const has_template = is_plain_object(template);

    // ---- pass 1: keys, types, per-field settings; build the lookup ----------
    const group_keys = new Set();
    const field_by_key = {};
    const ordered_fields = [];
    let field_count = 0;

    groups.forEach((group, group_index) => {
        const group_path = `groups.${group_index}`;
        if (!is_plain_object(group)) {
            add_error("invalid_group", group_path);
            return;
        }
        if (!KEY_PATTERN.group.test(String(group.key))) {
            add_error("invalid_key", `${group_path}.key`, { key: group.key });
        } else if (group_keys.has(group.key)) {
            add_error("duplicate_key", `${group_path}.key`, { key: group.key });
        } else {
            group_keys.add(group.key);
        }
        if (group.is_repeatable !== undefined && typeof group.is_repeatable !== "boolean") {
            add_error("invalid_flag", `${group_path}.is_repeatable`);
        }
        FLAGS.forEach((flag) => {
            if (group[flag] !== undefined && typeof group[flag] !== "boolean") {
                add_error("invalid_flag", `${group_path}.${flag}`);
            }
        });
        ["title", "description"].forEach((name) => {
            if (group[name] !== undefined && group[name] !== null && (typeof group[name] !== "string" || group[name].length > LIMITS.TEXTAREA_MAX)) {
                add_error("invalid_text", `${group_path}.${name}`);
            }
        });
        if (!Array.isArray(group.fields)) {
            add_error("invalid_group", `${group_path}.fields`);
            return;
        }

        group.fields.forEach((field, field_index) => {
            const path = `${group_path}.fields.${field_index}`;
            field_count += 1;
            if (!is_plain_object(field)) {
                add_error("invalid_field", path);
                return;
            }
            if (!KEY_PATTERN.field.test(String(field.key))) {
                add_error("invalid_key", `${path}.key`, { key: field.key });
            } else if (field_by_key[field.key]) {
                add_error("duplicate_key", `${path}.key`, { key: field.key });
            } else {
                field_by_key[field.key] = { field, group, group_index, path };
                ordered_fields.push(field_by_key[field.key]);
            }

            validate_field_type(field, path);
            validate_field_settings(field, path);
            if (field.validation_message !== undefined && field.validation_message !== null) {
                if (typeof field.validation_message !== "string" || field.validation_message.length > LIMITS.LABEL_MAX) {
                    add_error("invalid_text", `${path}.validation_message`, { max: LIMITS.LABEL_MAX });
                }
            }
        });
    });

    if (field_count > LIMITS.FIELDS_MAX) {
        add_error("too_many_fields", "groups", { max: LIMITS.FIELDS_MAX });
    }
    if (field_count === 0) {
        add_warning("empty_form", "groups");
    }

    function validate_field_type(field, path) {
        const type = field.type;
        if (typeof type !== "string") {
            add_error("invalid_type", `${path}.type`, { type });
            return;
        }
        if (type.startsWith(CUSTOM_FIELD_PREFIX)) {
            const custom_key = type.slice(CUSTOM_FIELD_PREFIX.length);
            if (!CUSTOM_FIELD_KEY_PATTERN.test(custom_key)) {
                add_error("invalid_type", `${path}.type`, { type });
            } else if (has_template && !custom_fields[custom_key]) {
                add_error("unsupported_custom_field", `${path}.type`, { type });
            }
            return;
        }
        if (!BASE_FIELD_TYPES.includes(type)) {
            add_error("invalid_type", `${path}.type`, { type });
        } else if (supported_types.length > 0 && !supported_types.includes(type)) {
            add_error("unsupported_type", `${path}.type`, { type });
        }
    }

    function validate_field_settings(field, path) {
        const type = field.type;
        if (!is_non_empty_string(field.label)) {
            add_error("label_required", `${path}.label`);
        } else if (field.label.length > LIMITS.LABEL_MAX) {
            add_error("invalid_text", `${path}.label`, { max: LIMITS.LABEL_MAX });
        }
        // error_message: the sentence shown when the field's own check refuses the value (its format, its range, its pattern)
        ["placeholder", "hint", "error_message"].forEach((name) => {
            if (field[name] !== undefined && field[name] !== null && (typeof field[name] !== "string" || field[name].length > LIMITS.LABEL_MAX)) {
                add_error("invalid_text", `${path}.${name}`, { max: LIMITS.LABEL_MAX });
            }
        });
        if (field.grid !== undefined && !GRID_SIZES.includes(field.grid)) {
            add_error("invalid_grid", `${path}.grid`, { grid: field.grid, allowed: GRID_SIZES });
        }
        if (field.direction !== undefined && field.direction !== null && !FIELD_DIRECTIONS.includes(field.direction)) {
            add_error("invalid_direction", `${path}.direction`, { allowed: FIELD_DIRECTIONS });
        }
        FLAGS.forEach((flag) => {
            if (field[flag] !== undefined && typeof field[flag] !== "boolean") {
                add_error("invalid_flag", `${path}.${flag}`);
            }
        });
        const data = field.data === undefined || field.data === null ? {} : field.data;
        if (!is_plain_object(data)) {
            add_error("invalid_data", `${path}.data`);
            return;
        }

        if (OPTION_FIELD_TYPES.includes(type)) {
            validate_options(field, path);
        } else if (field.options !== undefined && as_list(field.options).length > 0) {
            add_error("options_not_allowed", `${path}.options`, { type });
        }

        if (type === "text" || type === "textarea") {
            if (data.regex !== undefined && data.regex !== null && data.regex !== "") {
                const result = check_regex(data.regex);
                if (!result.ok) {
                    add_error("invalid_regex", `${path}.data.regex`, { reason: result.reason });
                }
            }
            if (type === "textarea" && data.rows !== undefined && !(Number.isInteger(data.rows) && data.rows >= 1 && data.rows <= 20)) {
                add_error("invalid_data", `${path}.data.rows`);
            }
        }
        if (type === "number") {
            ["min", "max"].forEach((name) => {
                if (data[name] !== undefined && data[name] !== null && !Number.isFinite(data[name])) {
                    add_error("invalid_data", `${path}.data.${name}`);
                }
            });
            if (Number.isFinite(data.min) && Number.isFinite(data.max) && data.min > data.max) {
                add_error("invalid_range", `${path}.data`, { min: data.min, max: data.max });
            }
            if (data.float !== undefined && typeof data.float !== "boolean") {
                add_error("invalid_data", `${path}.data.float`);
            }
        }
        if (type === "date" && data.mode !== undefined && !DATE_MODES.includes(data.mode)) {
            add_error("invalid_data", `${path}.data.mode`, { allowed: DATE_MODES });
        }
        // the formats a file field takes: some of FILE_FORMATS, each once (none given: all of them)
        if (type === "file" && data.accept !== undefined && data.accept !== null) {
            const accept = data.accept;
            if (!Array.isArray(accept) || accept.some((item) => !FILE_FORMATS.includes(item)) || new Set(accept).size !== accept.length) {
                add_error("invalid_data", `${path}.data.accept`, { allowed: FILE_FORMATS });
            }
        }
        if (typeof type === "string" && type.startsWith(CUSTOM_FIELD_PREFIX)) {
            const known = custom_fields[type.slice(CUSTOM_FIELD_PREFIX.length)];
            if (known && known.value_type !== undefined && !CUSTOM_VALUE_TYPES.includes(known.value_type)) {
                add_error("invalid_data", `${path}.type`, { value_type: known.value_type });
            }
        }
    }

    function validate_options(field, path) {
        const options = field.options;
        if (!Array.isArray(options)) {
            add_error("options_required", `${path}.options`);
            return;
        }
        if (field.type === "toggle") {
            if (options.length < 2 || options.length > 3) {
                add_error("toggle_options_count", `${path}.options`, { count: options.length });
            }
        } else if (options.length < 1) {
            add_error("options_required", `${path}.options`);
        }
        if (options.length > LIMITS.OPTIONS_MAX) {
            add_error("too_many_options", `${path}.options`, { max: LIMITS.OPTIONS_MAX });
        }
        const values = new Set();
        options.forEach((option, option_index) => {
            const option_path = `${path}.options.${option_index}`;
            if (!is_plain_object(option)) {
                add_error("invalid_option", option_path);
                return;
            }
            if (!KEY_PATTERN.option.test(String(option.value))) {
                add_error("invalid_key", `${option_path}.value`, { key: option.value });
            } else if (values.has(option.value)) {
                add_error("duplicate_key", `${option_path}.value`, { key: option.value });
            } else {
                values.add(option.value);
            }
            if (!is_non_empty_string(option.title)) {
                add_error("option_title_required", `${option_path}.title`);
            } else if (option.title.length > LIMITS.OPTION_TITLE_MAX) {
                add_error("invalid_text", `${option_path}.title`, { max: LIMITS.OPTION_TITLE_MAX });
            }
        });
    }

    // ---- pass 2: conditions --------------------------------------------------
    // Every condition owner: a group, or a field. Dependencies are field -> fields.
    const dependencies = {};
    const owners = [];
    groups.forEach((group, group_index) => {
        if (is_plain_object(group)) {
            owners.push({ node: group, path: `groups.${group_index}`, group, owner_field: null });
        }
    });
    ordered_fields.forEach((entry) => {
        owners.push({ node: entry.field, path: entry.path, group: entry.group, owner_field: entry.field });
    });

    owners.forEach(({ node, path, group, owner_field }) => {
        // a field also has the rules on its own value
        (owner_field ? CONDITION_LISTS.concat(VALIDATION_LIST) : CONDITION_LISTS).forEach((list_name) => {
            const list = node[list_name];
            if (list === undefined || list === null) {
                return;
            }
            if (!Array.isArray(list)) {
                add_error("invalid_condition", `${path}.${list_name}`);
                return;
            }
            if (list_name === VALIDATION_LIST && list.length > 0 && !VALIDATION_FIELD_TYPES.includes(node.type)) {
                add_error("validation_not_supported", `${path}.${list_name}`, { type: node.type });
                return;
            }
            list.forEach((item, item_index) => {
                const item_path = `${path}.${list_name}.${item_index}`;
                const conditions = [];
                if (is_plain_object(item) && Array.isArray(item.or)) {
                    if (item.or.length === 0) {
                        add_error("invalid_condition", item_path);
                    }
                    item.or.forEach((condition, condition_index) => {
                        if (is_plain_object(condition) && Array.isArray(condition.or)) {
                            add_error("nested_or", `${item_path}.or.${condition_index}`);
                        } else {
                            conditions.push({ condition, path: `${item_path}.or.${condition_index}` });
                        }
                    });
                } else {
                    conditions.push({ condition: item, path: item_path });
                }

                conditions.forEach(({ condition, path: condition_path }) => {
                    if (!is_plain_object(condition)) {
                        add_error("invalid_condition", condition_path);
                        return;
                    }
                    if (!OPERATORS.includes(condition.operator)) {
                        add_error("invalid_operator", `${condition_path}.operator`, { operator: condition.operator });
                        return;
                    }
                    if (!condition.first_field && condition.first_value === undefined) {
                        add_error("condition_incomplete", `${condition_path}.first_field`);
                    }
                    if (!UNARY_OPERATORS.includes(condition.operator) && !condition.second_field && condition.second_value === undefined) {
                        add_error("condition_incomplete", `${condition_path}.second_field`);
                    }
                    condition_field_refs(condition).forEach((ref) => {
                        const target = field_by_key[ref];
                        if (!target) {
                            add_error("unknown_reference", condition_path, { ref });
                            return;
                        }
                        if (target.group.is_repeatable === true && target.group !== group) {
                            add_error("reference_into_repeatable_group", condition_path, { ref });
                        } else if (target.group.is_repeatable === true && !owner_field) {
                            // the group's own conditions are "outside" the group
                            add_error("reference_into_repeatable_group", condition_path, { ref });
                        }
                        if (list_name === VALIDATION_LIST) {
                            // a rule reads values at the end; it is no dependency of any state
                            return;
                        }
                        // A group's conditions apply to each of its fields.
                        const dependents = owner_field ? [owner_field.key] : as_list(group.fields).map((field) => field?.key);
                        dependents.filter((key) => typeof key === "string").forEach((key) => {
                            if (!dependencies[key]) {
                                dependencies[key] = new Set();
                            }
                            dependencies[key].add(ref);
                        });
                    });
                });
            });
        });
    });

    // ---- cycles: A reads B and B reads A (a field reading itself too) --------
    const state = {};
    const reported = new Set();
    const visit = (key, trail) => {
        state[key] = 1;
        const next_keys = dependencies[key] ? Array.from(dependencies[key]) : [];
        next_keys.forEach((next) => {
            if (!field_by_key[next]) {
                return;
            }
            if (state[next] === 1) {
                const cycle = trail.slice(trail.indexOf(next)).concat(next);
                const signature = cycle.slice().sort().join(",");
                if (!reported.has(signature)) {
                    reported.add(signature);
                    add_error("circular_dependency", field_by_key[next].path, { cycle });
                }
            } else if (!state[next]) {
                visit(next, trail.concat(next));
            }
        });
        state[key] = 2;
    };
    Object.keys(dependencies).forEach((key) => {
        if (!state[key] && field_by_key[key]) {
            visit(key, [key]);
        }
    });

    // ---- cities need a state before them --------------------------------------
    let seen_state = false;
    ordered_fields.forEach((entry) => {
        if (entry.field.type === "state") {
            seen_state = true;
        } else if (entry.field.type === "city" && !seen_state) {
            add_warning("city_without_state", entry.path);
        }
    });

    return { valid: errors.length === 0, errors, warnings };
};

export {
    validate_form_structure,
};
